import "./styles.css";
import { Alert, Card, Col, Row, Typography, message } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  EXPORT_POST_COSTING_REPORT,
  VIEW_POST_COSTING_REPORT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";

import { commaSeparateNumber } from "utils/functions";
import { cglFormatDate } from "utils/functions";
import { PostCostingReportPreviewContext } from "../../../common/SearchForm/FilterFields/PostCostingReportFilter";


const PostCostingReport = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isPreviewClicked, setIsPreviewClicked] = useState(true);

  const [responseDataRow, setResponseDataRow] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Round 2 (B, R3): mounted-ref + request-sequence guards for previewFunction, which
  // PostCostingReportFilter now also calls once on mount for the default contract.
  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  const lastFilterValuesRef = useRef(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const exportDetails = async (filterValues) => {
    if (!Object.values(filterValues).some((val) => val === undefined)) {
      setLoading(true);
      const query = `${EXPORT_POST_COSTING_REPORT}`;
      const bodyData = {
        ...filterValues,
      };
      const response = await getData(
        query,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response && response?.data) {
        // Download the file
        const outputFilename = `post-costing-report.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setLoading(false);
        message.success("Post Costing Report Exported Successfully");
        refetch();
      } else {
        setLoading(false);
        setIsPreviewClicked(true);
        message.error("Something went wrong");
      }
    } else {
      setIsPreviewClicked(true);
    }
  };

  const previewFunction = async (filterValues) => {
    const myReq = ++reqIdRef.current;
    lastFilterValuesRef.current = filterValues;
    setFetchError(null);
    setLoading(true);

    const bodyData = {
      ...filterValues,
      preview: 1,
    };

    const query = `${VIEW_POST_COSTING_REPORT}`;
    const response = await postData(query, bodyData);

    // Round 2 (B, R3): mounted guard + stale-response race guard (round-1 C1 pattern).
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.code === 200) {
      setResponseDataRow(response?.data?.data_row);
      const processedData = [];

      // Group data by item type first
      const groupedByItem = {};

      response?.data?.data_row.forEach((data) => {
        const firstImportPi = data?.import_pis?.[0] || null;
        const itemType = firstImportPi?.goods_description || "Unknown";

        if (!groupedByItem[itemType]) {
          groupedByItem[itemType] = [];
        }

        const invoicesLength = data?.invoices?.length || 0;
        const importPisLength = data?.import_pis?.length || 0;
        const maxLength = Math.max(invoicesLength, importPisLength, 1);

        // Create rows based on the maximum length
        for (let i = 0; i < maxLength; i++) {
          const invoice = data?.invoices?.[i] || null;
          const importPi = data?.import_pis?.[i] || null;

          groupedByItem[itemType].push({
            id: data?.id,
            acceptance: data?.acceptance,
            b2b_lc_number: data?.b2b_lc_number,
            total_lc_value: data?.total_lc_value,
            goods_description: itemType,
            supplier_name: importPi?.supplier_info?.name || null,
            pi_number: importPi?.pi_number || null,
            pi_value: importPi?.pi_value || null,
            invoice_number: invoice?.invoice_number || null,
            invoice_value: invoice?.invoice_value || null,
            rowIndex: i,
            maxLength: maxLength,
            itemType: itemType,
          });
        }
      });

      // Flatten grouped data maintaining item type order
      Object.keys(groupedByItem).forEach((itemType) => {
        processedData.push(...groupedByItem[itemType]);
      });

      const table1 = [
        {
          name: "Company",
          data: response.data.company,
        },
        {
          name: "Buyer Name",
          data: response.data.buyer_name,
        },
        {
          name: "Master LC No",
          data: response.data.master_lc_no,
        },
        {
          name: "Master LC Date",
          data: cglFormatDate(response.data.master_lc_date),
        },

        {
          name: "Sales Contract Date",
          data: cglFormatDate(response.data.sales_contract_date),
        },
        {
          name: "Sales Contract No",
          data: response.data.sales_contract_no,
        },
        {
          name: "Order Qty",
          data: response.data.order_qty,
        },
        {
          name: "Order Value",
          data: response.data.order_value,
        },
        {
          name: "FOB Value",
          data: response.data.fob,
        },
        {
          name: "B2B Value",
          data: response.data.b2b_value,
        },
      ];
      const table2 = [
        {
          name: "Shipment Closing On",
          data: response.data.shipment_closing_on,
        },
        {
          name: "CM PER PC",
          data: response.data.cm_per_pc,
        },
        {
          name: "Total CM",
          data: response.data.total_cm,
        },
        {
          name: "Other Charges",
          data: response.data.other_charges,
        },
        {
          name: "Shipment Quantity",
          data: response.data.shipment_quantity,
        },
        {
          name: "Export Value",
          data: response.data.export_value,
        },
        {
          name: "Excess Shipment",
          data: response.data.excess_shipment,
        },
        {
          name: "Excess Short Shipment Value",
          data: response.data.excess_short_shipment_value,
        },
        {
          name: "Total B2B Opended",
          data: response.data.total_btb_opended,
        },
        {
          name: "Savings From B2B",
          data: response.data.savings_from_btb,
        },
      ];
      setTableData({
        table1: table1,
        table2: table2,
        table3: processedData,
        table4: {
          cutting_qty: response?.data?.cutting_qty,
          ex_factory_qty: response?.data?.ex_factory_qty,
          reject_qty: response?.data?.reject_qty,
          good_qty: response?.data?.good_qty,
        },
        netSaving: response?.data?.net_savings_loss,
        summary: response?.data?.summary,
      });
      setIsPreviewClicked(false);
      setLoading(false);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      setIsPreviewClicked(true);
      message.error("Something went wrong");
    }
  };
  // Effects
  // useEffect(() => {
  //   getAllDetails();
  // }, [refresh, getAllDetails]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const firstColumns = [
    {
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      dataIndex: "data",
      key: "data",
    },
  ];

  const lastColumns = [
    {
      title: "Cutting Qty",
      dataIndex: "cutting_qty",
      key: "cutting_qty",
    },
    {
      title: "Ex Factory Qty",
      dataIndex: "ex_factory_qty",
      key: "ex_factory_qty",
    },
    {
      title: "Reject Qty",
      dataIndex: "reject_qty",
      key: "reject_qty",
    },
    {
      title: "Good Qty",
      dataIndex: "good_qty",
      key: "good_qty",
    },
  ];

  const summaryColumns = [
    {
      title: "Description",
      dataIndex: "goods_description",
      key: "goods_description",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Total LC Value (USD)",
      dataIndex: "sum_total_lc_value",
      key: "sum_total_lc_value",
      align: "right",
      render: (val) => commaSeparateNumber(val),
    },
    {
      title: "Total Invoice Value (USD)",
      dataIndex: "sum_total_invoice_value",
      key: "sum_total_invoice_value",
      align: "right",
      render: (val) => commaSeparateNumber(val),
    },
    {
      title: "Total Payment (USD)",
      dataIndex: "sum_acceptance",
      key: "sum_acceptance",
      align: "right",
      render: (val) => commaSeparateNumber(val),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={tableData}
        fetchTableData={exportDetails}
        filterName="post-costing-report"
        searchButtonText="Export"
        isPreview={true}
        previewFunction={previewFunction}
      />
    ),
    [showFilter],
  );

  // Add these helper functions for table3
  const distributeRowSpans = (items, totalRows) => {
    if (!items?.length) return [];
    const baseSpan = Math.floor(totalRows / items.length);
    const extraSpans = totalRows % items.length;

    return items.map((item, idx) => ({
      ...item,
      rowSpan: baseSpan + (idx < extraSpans ? 1 : 0),
    }));
  };

  const renderTable3 = () => {
    return responseDataRow.map((item, groupIndex) => {
      const maxRows = Math.max(
        item.import_pis?.length || 0,
        item.invoices?.length || 0,
      );

      // Distribute rowspans evenly
      const distributedPIs = distributeRowSpans(item.import_pis, maxRows);
      const distributedInvoices = distributeRowSpans(item.invoices, maxRows);

      let piIndex = 0;
      let invoiceIndex = 0;
      let piRowsUsed = 0;
      let invoiceRowsUsed = 0;

      return Array(maxRows)
        .fill(null)
        .map((_, rowIndex) => {
          // Get current PI and Invoice based on distributed spans
          const showPI = piRowsUsed === 0;
          const showInvoice = invoiceRowsUsed === 0;
          const currentPI = showPI ? distributedPIs[piIndex] : null;
          const currentInvoice = showInvoice
            ? distributedInvoices[invoiceIndex]
            : null;

          // Increment counters
          if (showPI) piRowsUsed = currentPI?.rowSpan || 0;
          if (showInvoice) invoiceRowsUsed = currentInvoice?.rowSpan || 0;

          piRowsUsed--;
          invoiceRowsUsed--;

          if (piRowsUsed === 0) piIndex++;
          if (invoiceRowsUsed === 0) invoiceIndex++;

          return (
            <tr key={`${item.id}-${rowIndex}`}>
              {rowIndex === 0 && (
                <td rowSpan={maxRows} style={{ backgroundColor: "#fafafa" }}>
                  {item.import_pis[0]?.goods_description}
                </td>
              )}
              {showPI && (
                <>
                  <td rowSpan={currentPI?.rowSpan}>
                    {currentPI?.supplier_info?.name}
                  </td>
                  <td rowSpan={currentPI?.rowSpan}>{currentPI?.pi_number}</td>
                  <td rowSpan={currentPI?.rowSpan}>
                    {commaSeparateNumber(currentPI?.pi_value)}
                  </td>
                </>
              )}
              {rowIndex === 0 && (
                <>
                  <td rowSpan={maxRows} style={{ backgroundColor: "#fafafa" }}>
                    {item.b2b_lc_number}
                  </td>
                  <td rowSpan={maxRows} style={{ backgroundColor: "#fafafa" }}>
                    {commaSeparateNumber(item.total_lc_value)}
                  </td>
                </>
              )}
              {showInvoice && (
                <>
                  <td rowSpan={currentInvoice?.rowSpan}>
                    {currentInvoice?.invoice_number}
                  </td>
                  <td rowSpan={currentInvoice?.rowSpan}>
                    {commaSeparateNumber(currentInvoice?.invoice_value)}
                  </td>
                </>
              )}
              {rowIndex === 0 && (
                <td rowSpan={maxRows} style={{ backgroundColor: "#fafafa" }}>
                  {item.acceptance}
                </td>
              )}
            </tr>
          );
        });
    });
  };

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportDetails}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Post Costing Report</Title>}
          />
        }
        content={
          <>
            <div className="containt-body">
              {/* Fix round 1 (B, I2): the Provider must wrap the Card OUTSIDE the
                  [showFilter]-keyed useMemo, not inside it, so PostCostingReportFilter's
                  useContext always sees the current render's previewFunction closure
                  instead of whichever one existed the last time showFilter changed.
                  Matches SalesContractCostReport/index.js's placement. */}
              <PostCostingReportPreviewContext.Provider value={previewFunction}>
                <Card>{memoizedCustomFilter}</Card>
              </PostCostingReportPreviewContext.Provider>
              <Row justify={"space-between"}>
                {isPreviewClicked && (
                  <Col span={24}>
                    <Alert
                      style={{ margin: "10px 50px" }}
                      message="Please select Sales Contract to view Post Costing Report."
                      type="warning"
                    />
                  </Col>
                )}
                {tableData?.table1 && tableData?.table2 ? (
                  <>
                    <Col span={24}>
                      <Title
                        level={4}
                        style={{
                          textAlign: "center",
                          backgroundColor: "#1b4b72",
                          margin: 0,
                          padding: 8,
                          color: "#fff",
                        }}
                      >
                        Post Costing Report
                      </Title>
                    </Col>
                    <Col span={10}>
                      <ListTable
                        loading={loading}
                        error={fetchError}
                        onRetry={() => previewFunction(lastFilterValuesRef.current)}
                        emptyText="No records to show yet."
                        dataSource={tableData?.table1}
                        showHeader={false}
                        columns={firstColumns}
                        scroll={{ y: "max-content" }}
                        bordered
                        pagination={false}
                      />
                    </Col>
                    <Col span={10}>
                      <ListTable
                        loading={loading}
                        error={fetchError}
                        onRetry={() => previewFunction(lastFilterValuesRef.current)}
                        emptyText="No records to show yet."
                        showHeader={false}
                        dataSource={tableData?.table2}
                        columns={firstColumns}
                        scroll={{ y: "max-content" }}
                        bordered
                        pagination={false}
                      />
                    </Col>
                    <Col span={24}>
                      <Title
                        level={4}
                        style={{
                          textAlign: "center",
                          border: "1px solid #f0f0f0",
                          margin: 0,
                          padding: 8,
                        }}
                      >
                        {"Net Savings / (Loss):"}{" "}
                        {tableData?.netSaving ? tableData?.netSaving : 0}
                      </Title>
                    </Col>
                  </>
                ) : null}
              </Row>

              <br />
              {tableData?.table3 && (
                <div className="custom-table-wrapper">
                  {loading && (
                    <div className="table-loading-overlay">Loading...</div>
                  )}
                  <div className="table-container">
                    <table className="custom-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Supplier Name</th>
                          <th>PI No.</th>
                          <th>PI Value</th>
                          <th>B2B LC No.</th>
                          <th>B2B Value (USD)</th>
                          <th>Invoice No.</th>
                          <th>Invoice Value (USD)</th>
                          <th>Payment</th>
                        </tr>
                      </thead>
                      <tbody>{renderTable3()}</tbody>
                    </table>
                  </div>
                </div>
              )}
              <br />

              {tableData?.summary?.grouped?.length > 0 && (
                <Row justify="center" style={{ marginTop: 8, marginBottom: 8 }}>
                  <Col span={14}>
                    <ListTable
                      loading={loading}
                      error={fetchError}
                      onRetry={() => previewFunction(lastFilterValuesRef.current)}
                      emptyText="No records to show yet."
                      dataSource={[
                        ...tableData.summary.grouped,
                        {
                          goods_description: "Grand Total",
                          sum_total_lc_value: tableData.summary.sum_total_lc_value,
                          sum_total_invoice_value: tableData.summary.sum_total_invoice_value,
                          sum_acceptance: tableData.summary.sum_acceptance,
                        },
                      ]}
                      columns={summaryColumns}
                      rowKey="goods_description"
                      bordered
                      pagination={false}
                      rowClassName={(record) =>
                        record.goods_description === "Grand Total"
                          ? "summary-grand-total-row"
                          : ""
                      }
                      title={() => (
                        <strong style={{ fontSize: 14 }}>Summary</strong>
                      )}
                    />
                  </Col>
                </Row>
              )}

              <br />

              {tableData?.table4 && (
                <Row justify={"center"} style={{ marginTop: 20 }}>
                  <Col span={8}>
                    <ListTable
                      loading={loading}
                      error={fetchError}
                      onRetry={() => previewFunction(lastFilterValuesRef.current)}
                      emptyText="No records to show yet."
                      dataSource={[tableData?.table4]}
                      columns={lastColumns}
                      bordered
                      scroll={"max-content"}
                      pagination={false}
                    />
                  </Col>
                </Row>
              )}
            </div>
          </>
        }
      />
    </>
  );
};

export default PostCostingReport;

// {tableData?.table4 && (
//   <Row justify={"center"} style={{ marginTop: 20 }}>
//     <Col span={8}>
//       <Table
//         loading={loading}
//         dataSource={[tableData?.table4]}
//         columns={lastColumns}
//         bordered
//         scroll={"max-content"}
//         pagination={false}
//       />
//     </Col>
//   </Row>
// )}

// {tableData?.table4 && (
//   <Row justify={"center"} style={{ marginTop: 20 }}>
//     <Col span={8}>
//       <Table
//         loading={loading}
//         dataSource={[tableData?.table4]}
//         columns={lastColumns}
//         bordered
//         scroll={"max-content"}
//         pagination={false}
//       />
//     </Col>
//   </Row>
// )}
